import { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { API_BASE } from '../../config/env';
import LandingLayout from './LandingLayout';
import { useLandingAnimations } from './useLandingAnimations';
import { useI18n } from '../../i18n/LanguageContext';
import { buildLandingPathForMarket, resolveLandingContext, type LandingMarket } from './regional';
import { getLandingCatalogPage, getLandingTopNav, type LandingCatalogPageKey, type LandingContentLocale } from './siteContent';

const CAL_EMBED_URL = 'https://cal.com/elen-one';

const pricingPlans: PricingPlanCard[] = [
  {
    name: 'Starter',
    audience: 'Solo clinics getting organized',
    monthlyUsd: 129,
    unit: 'provider',
    cta: 'Start with Starter',
    featured: false,
    points: ['Calendar and online booking', 'Patient records and intake forms', 'Basic billing and payments', 'Email support'],
  },
  {
    name: 'Practice',
    audience: 'Growing teams with front desk flow',
    monthlyUsd: 249,
    unit: 'provider',
    cta: 'Choose Practice',
    featured: true,
    points: ['Everything in Starter', 'Team queues and room coordination', 'Packages, memberships, and deposits', 'Operational reporting'],
  },
  {
    name: 'Multi-site',
    audience: 'Groups that need oversight',
    monthlyUsd: null,
    unit: 'operation',
    customCadence: 'for multi-location operations',
    cta: 'Talk to sales',
    featured: false,
    points: ['Everything in Practice', 'Multi-location controls', 'Advanced permissions and audit trails', 'Implementation planning'],
  },
  {
    name: 'Clinical AI',
    audience: 'Care teams using AI workflows',
    monthlyUsd: 79,
    unit: 'provider add-on',
    cta: 'Add Clinical AI',
    featured: false,
    points: ['AI visit summaries', 'Clinical context cards', 'Follow-up drafting', 'Governance controls'],
  },
  {
    name: 'Growth',
    audience: 'Clinics improving retention',
    monthlyUsd: 99,
    unit: 'location add-on',
    cta: 'Add Growth',
    featured: false,
    points: ['Campaign templates', 'Recall and reactivation lists', 'Review requests', 'Referral tracking'],
  },
  {
    name: 'Enterprise',
    audience: 'Complex teams and custom rollout',
    monthlyUsd: null,
    unit: 'contract',
    customCadence: 'tailored contract',
    cta: 'Book a demo',
    featured: false,
    points: ['Custom onboarding', 'Security and procurement support', 'Integration planning', 'Dedicated success path'],
  },
];

type BillingCycle = 'monthly' | 'annual';

const pricingMarkets = [
  {
    key: 'uk',
    flag: '🇬🇧',
    label: 'UK',
    currency: 'GBP',
    locale: 'en-GB',
    monthlyPrices: { Starter: 99, Practice: 189, 'Clinical AI': 59, Growth: 75 },
  },
  {
    key: 'us',
    flag: '🇺🇸',
    label: 'US',
    currency: 'USD',
    locale: 'en-US',
    monthlyPrices: { Starter: 129, Practice: 249, 'Clinical AI': 79, Growth: 99 },
  },
  {
    key: 'ca',
    flag: '🇨🇦',
    label: 'CA',
    currency: 'CAD',
    locale: 'en-CA',
    monthlyPrices: { Starter: 179, Practice: 349, 'Clinical AI': 109, Growth: 139 },
  },
  {
    key: 'au',
    flag: '🇦🇺',
    label: 'AU',
    currency: 'AUD',
    locale: 'en-AU',
    monthlyPrices: { Starter: 179, Practice: 349, 'Clinical AI': 109, Growth: 139 },
  },
  {
    key: 'eu',
    flag: '🇪🇺',
    label: 'EU',
    currency: 'EUR',
    locale: 'en-IE',
    monthlyPrices: { Starter: 109, Practice: 215, 'Clinical AI': 69, Growth: 85 },
  },
  // LATAM markets (GL-H9). Anchors from docs/go-live-audit-2026-06/03-COMPETITIVE-
  // POSITIONING-PRICING-MARKETING.md, mapped to existing tiers by order
  // (entry→Starter, mid→Practice, top→Multi-site/custom). Add-on (Clinical AI,
  // Growth) prices are scaled to local equivalents.
  // TODO: owner to confirm final pricing
  {
    key: 'latam',
    flag: '🌎',
    label: 'LATAM',
    currency: 'USD',
    locale: 'es-419',
    monthlyPrices: { Starter: 19, Practice: 49, 'Clinical AI': 15, Growth: 19 },
  },
  {
    key: 'mx',
    flag: '🇲🇽',
    label: 'MX',
    currency: 'MXN',
    locale: 'es-MX',
    monthlyPrices: { Starter: 399, Practice: 899, 'Clinical AI': 299, Growth: 399 },
  },
  {
    key: 'br',
    flag: '🇧🇷',
    label: 'BR',
    currency: 'BRL',
    locale: 'pt-BR',
    monthlyPrices: { Starter: 99, Practice: 199, 'Clinical AI': 79, Growth: 99 },
  },
] as const;

const includedPricingItems = [
  { label: 'Secure cloud hosting', icon: 'ph-cloud-check' },
  { label: 'Role-based access control', icon: 'ph-lock-key' },
  { label: 'Patient intake and consent forms', icon: 'ph-clipboard-text' },
  { label: 'Scheduling and calendar workflows', icon: 'ph-calendar-check' },
  { label: 'Invoices, balances, and payments', icon: 'ph-credit-card' },
  { label: 'Operational dashboards', icon: 'ph-chart-line-up' },
  { label: 'Patient messaging workflows', icon: 'ph-chat-circle-text' },
  { label: 'Implementation checklist', icon: 'ph-list-checks' },
];

const pricingAddOns = [
  { name: 'Advanced implementation', detail: 'Migration planning, workflow mapping, and launch support for complex teams.', price: 'Custom' },
  { name: 'Custom integrations', detail: 'Connect accounting, marketing, analytics, or local systems with a scoped integration plan.', price: 'Scoped' },
  { name: 'Dedicated success', detail: 'Quarterly workflow reviews and rollout support for multi-location groups.', price: 'Custom' },
];

const comparisonRows = [
  ['Online booking', true, true, true, true],
  ['Calendar and reminders', true, true, true, true],
  ['Patient records', true, true, true, true],
  ['Forms and consent', true, true, true, true],
  ['Billing and payments', true, true, true, true],
  ['Packages and memberships', false, true, true, true],
  ['Team queues and handoffs', false, true, true, true],
  ['Room and resource coordination', false, true, true, true],
  ['Operational reporting', false, true, true, true],
  ['Multi-location oversight', false, false, true, true],
  ['Advanced permissions', false, false, true, true],
  ['Audit and governance support', false, false, true, true],
  ['Clinical AI workflows', false, 'Add-on', 'Add-on', true],
  ['Growth campaigns', false, 'Add-on', 'Add-on', true],
  ['Custom implementation plan', false, false, true, true],
];

const pricingQuestions = [
  {
    question: 'Can we start small and add more later?',
    answer: 'Yes. Most teams start with the workflows they need now, then expand into AI, growth, reporting, or multi-site controls as operations mature.',
  },
  {
    question: 'Is implementation included?',
    answer: 'Every plan includes a launch checklist. Larger or more complex clinics can add scoped implementation support.',
  },
  {
    question: 'Do prices include payment processing fees?',
    answer: 'Software pricing is separate from card processing, messaging, and third-party service fees where those apply.',
  },
  {
    question: 'Do you support custom contracts?',
    answer: 'Yes. Multi-location, enterprise, and integration-heavy deployments can be quoted around your rollout and procurement needs.',
  },
];

const featureMetrics = [
  { value: '01', label: 'One operating layer' },
  { value: '06', label: 'Core workflow areas' },
  { value: '24/7', label: 'Patient self-service' },
];

const featureSpotlights = [
  {
    title: 'Scheduling that stays connected to the clinic day',
    body: 'Calendar, rooms, reminders, intake, and follow-up work from the same operational context.',
    image: '/landing-captures/scheduling-standalone.png',
    alt: 'Elen1 scheduling workspace',
    path: '/calendar-booking',
  },
  {
    title: 'Billing that stays close to the visit',
    body: 'Invoices, balances, deposits, memberships, and payments stay tied to the patient workflow.',
    image: '/landing-captures/billing-standalone.png',
    alt: 'Elen1 billing workspace',
    path: '/billing-payments',
  },
  {
    title: 'Reporting built from real operational activity',
    body: 'Leadership can see patient activity, utilization, revenue, and team performance without side reports.',
    image: '/landing-captures/reports-standalone.png',
    alt: 'Elen1 reports workspace',
    path: '/reports-insights',
  },
];

const featureSubpageKeys = new Set<LandingCatalogPageKey>([
  'emr-ehr',
  'clinical-ai',
  'patient-portal',
  'calendar-booking',
  'billing-payments',
  'marketing-growth',
  'multi-site-operations',
  'reports-insights',
]);

const aboutValues = [
  {
    title: 'Built around the clinic day',
    body: 'Elen1 is designed for the way care teams actually move: booking, intake, visits, payments, follow-up, and reporting in one connected flow.',
    icon: 'ph-calendar-check',
  },
  {
    title: 'Practical technology, not extra admin',
    body: 'Every workflow is meant to reduce duplicate entry, surface the next action, and give each role the context they need without another tool to manage.',
    icon: 'ph-sparkle',
  },
  {
    title: 'A calmer operating layer',
    body: 'Owners, managers, practitioners, and front desk teams get shared visibility across patient care and business operations.',
    icon: 'ph-heartbeat',
  },
];

const aboutPrinciples = [
  'Make clinic operations easier to see, assign, and improve.',
  'Keep patient context close to scheduling, charting, billing, and follow-up.',
  'Support growing teams without forcing them into fragmented systems.',
];

function FeaturesPageContent({ toMarketPath, locale }: { toMarketPath: (path: string) => string; locale: LandingContentLocale }) {
  const isSpanish = locale === 'es';
  const featureGroups = getLandingTopNav(locale).find((item) => item.key === 'features')?.groups ?? [];
  const metrics = isSpanish
    ? [
      { value: '01', label: 'Una capa operativa' },
      { value: '06', label: 'Áreas de flujo principales' },
      { value: '24/7', label: 'Autoservicio del paciente' },
    ]
    : featureMetrics;
  const spotlights = isSpanish
    ? [
      {
        title: 'Agenda conectada con el día clínico',
        body: 'Calendario, salas, recordatorios, admisión y seguimiento trabajan desde el mismo contexto operativo.',
        image: '/landing-captures/scheduling-standalone.png',
        alt: 'Espacio de agenda de Elen1',
        path: '/calendar-booking',
      },
      {
        title: 'Facturación cerca de la visita',
        body: 'Facturas, saldos, depósitos, membresías y pagos permanecen ligados al flujo del paciente.',
        image: '/landing-captures/billing-standalone.png',
        alt: 'Espacio de facturación de Elen1',
        path: '/billing-payments',
      },
      {
        title: 'Reportes basados en actividad real',
        body: 'La dirección puede ver actividad, utilización, ingresos y rendimiento del equipo sin reportes paralelos.',
        image: '/landing-captures/reports-standalone.png',
        alt: 'Reportes de Elen1',
        path: '/reports-insights',
      },
    ]
    : featureSpotlights;

  return (
    <>
      <section className="features-page-hero">
        <div className="features-page-shell features-page-hero-grid">
          <div className="features-page-hero-copy">
            <p className="features-page-eyebrow">{isSpanish ? 'Funciones de Elen1' : 'Elen1 features'}</p>
            <h1>{isSpanish ? 'Un sistema clínico conectado con cada flujo en contexto.' : 'One connected clinic system with every workflow in context.'}</h1>
            <p>
              {isSpanish
                ? 'Reúne agenda, historia clínica, facturación, reportes, IA y comunicación con pacientes en una capa operativa más clara para que el equipo avance sin unir herramientas a mano.'
                : 'Bring scheduling, records, billing, reporting, AI, and patient communication into one calmer operating layer so the team can move through the day without stitching tools together.'}
            </p>
            <div className="features-page-actions">
              <Link to={toMarketPath('/book-demo')} className="btn-lime">{isSpanish ? 'Reservar una demo' : 'Book a demo'}</Link>
              <Link to={toMarketPath('/pricing')} className="features-page-secondary">{isSpanish ? 'Ver precios' : 'View pricing'}</Link>
            </div>
            <div className="features-page-metrics">
              {metrics.map((metric) => (
                <span key={metric.label} className="landing-animate-card">
                  <strong>{metric.value}</strong>
                  <small>{metric.label}</small>
                </span>
              ))}
            </div>
          </div>

          <div className="features-page-hero-visual">
            <div className="features-page-hero-glow" aria-hidden="true"></div>
            <div className="features-page-screen">
              <img src="/landing-generated/features-workflow-hero.png" alt={isSpanish ? 'Equipo clínico usando flujos conectados de Elen1' : 'Clinic team using Elen1 connected workflows'} />
            </div>
            <div className="features-page-floating-card features-page-floating-card-a landing-animate-card">
              <i className="ph ph-calendar-check"></i>
              <span>{isSpanish ? 'Reserva, admisión y seguimiento permanecen conectados' : 'Booking, intake, and follow-up stay connected'}</span>
            </div>
            <div className="features-page-floating-card features-page-floating-card-b landing-animate-card">
              <i className="ph ph-chart-line-up"></i>
              <span>{isSpanish ? 'Ingresos y operaciones visibles en un solo lugar' : 'Revenue and operations visible in one place'}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="features-page-workflows">
        <div className="features-page-shell">
          <div className="features-page-section-heading">
            <p>{isSpanish ? 'Cobertura de flujos' : 'Workflow coverage'}</p>
            <h2>{isSpanish ? 'Explora las partes de Elen1 que las clínicas usan todos los días.' : 'Explore the parts of Elen1 clinics use every day.'}</h2>
          </div>
          <div className="features-page-workflow-grid">
            {featureGroups.map((group) => (
              <article key={group.title} className="features-page-workflow-group landing-animate-card">
                <div className="features-page-workflow-header">
                  <span>{group.title}</span>
                  <b>{String(group.items.length).padStart(2, '0')}</b>
                </div>
                <div className="features-page-workflow-links">
                  {group.items.map((item) => {
                    const linkedPage = getLandingCatalogPage(item.key, locale);
                    return (
                      <Link key={item.key} to={toMarketPath(linkedPage.path)}>
                        <i className={`ph ${item.icon}`}></i>
                        <span>
                          <strong>{item.label}</strong>
                          <small>{item.description}</small>
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="features-page-operating-layer">
        <div className="features-page-shell features-page-operating-grid">
          <div className="features-page-operating-copy">
            <p className="features-page-eyebrow">{isSpanish ? 'La forma Elen1' : 'The Elen1 way'}</p>
            <h2>{isSpanish ? 'Contexto compartido desde la reserva hasta el pago.' : 'Shared context from booking to billing.'}</h2>
            <p>
              {isSpanish
                ? 'Elen1 está diseñado alrededor del día clínico, no de módulos desconectados. Cada flujo mantiene el contexto del paciente, la responsabilidad del equipo, el estado financiero y las próximas acciones cerca del trabajo.'
                : 'Elen1 is designed around the clinic day, not around disconnected modules. Each workflow keeps patient context, team ownership, financial status, and next actions close to the work.'}
            </p>
            <div className="features-page-check-list">
              {(isSpanish
                ? [
                  'Da a recepción, clínicos, gerentes y propietarios la misma vista operativa',
                  'Reduce doble captura entre agenda, historia clínica, facturación y seguimiento',
                  'Agrega IA y crecimiento sin crear otro panel que administrar',
                ]
                : [
                  'Give front desk, clinicians, managers, and owners the same operating picture',
                  'Reduce duplicate entry between calendar, chart, billing, and follow-up',
                  'Add AI and growth workflows without creating another dashboard to manage',
                ]).map((item) => (
                <span key={item}><i className="ph ph-check"></i>{item}</span>
              ))}
            </div>
          </div>
          <div className="features-page-operating-visual landing-animate-card">
            <img src="/landing-captures/dashboard-standalone.png" alt={isSpanish ? 'Panel de Elen1 con operaciones clínicas conectadas' : 'Elen1 dashboard showing connected clinic operations'} />
          </div>
        </div>
      </section>

      <section className="features-page-spotlights">
        <div className="features-page-shell">
          <div className="features-page-section-heading">
            <p>{isSpanish ? 'Dentro del producto' : 'Inside the product'}</p>
            <h2>{isSpanish ? 'Superficies de trabajo creadas para sentirse conectadas, no añadidas.' : 'Workflow surfaces built to feel connected, not bolted on.'}</h2>
          </div>
          <div className="features-page-spotlight-grid">
            {spotlights.map((spotlight) => (
              <Link key={spotlight.title} to={toMarketPath(spotlight.path)} className="features-page-spotlight-card landing-animate-card">
                <img src={spotlight.image} alt={spotlight.alt} loading="lazy" />
                <span>
                  <strong>{spotlight.title}</strong>
                  <small>{spotlight.body}</small>
                </span>
                <i className="ph ph-arrow-up-right"></i>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="features-page-demo-band">
        <div className="features-page-shell">
          <div>
            <p className="features-page-eyebrow">{isSpanish ? 'Míralo en tu flujo' : 'See it around your workflow'}</p>
            <h2>{isSpanish ? '¿Quieres saber qué funciones importan primero para tu clínica?' : 'Want to know which features matter first for your clinic?'}</h2>
          </div>
          <Link to={toMarketPath('/book-demo')} className="btn-lime">{isSpanish ? 'Reservar demo de flujo' : 'Book a workflow demo'}</Link>
        </div>
      </section>
    </>
  );
}

function FeatureSubpageContent({
  page,
  toMarketPath,
  locale,
}: {
  page: ReturnType<typeof getLandingCatalogPage>;
  toMarketPath: (path: string) => string;
  locale: LandingContentLocale;
}) {
  const isSpanish = locale === 'es';
  const heroImage = page.screenshots?.[0] ?? {
    src: '/landing-generated/features-workflow-hero.png',
    alt: isSpanish ? 'Flujo clínico conectado en Elen1' : 'Elen1 connected clinic workflow',
    title: isSpanish ? 'Flujo conectado' : 'Connected workflow',
    body: isSpanish ? 'Una vista conectada del trabajo que tu clínica necesita gestionar cada día.' : 'A connected view of the work your clinic needs to manage every day.',
  };
  const secondaryImage = page.screenshots?.[1] ?? heroImage;
  const featureIcon = getLandingTopNav(locale)
    .flatMap((navItem) => navItem.groups ?? [])
    .flatMap((group) => group.items)
    .find((item) => item.key === page.path.slice(1))?.icon ?? 'ph-squares-four';

  return (
    <>
      <section className="subfeature-hero">
        <div className="features-page-shell subfeature-hero-grid">
          <div className="subfeature-hero-copy">
            <p className="features-page-eyebrow">{page.eyebrow}</p>
            <h1>{page.title}</h1>
            <p>{page.subtitle}</p>
            <div className="features-page-actions">
              <Link to={toMarketPath('/book-demo')} className="btn-lime">{isSpanish ? 'Reservar una demo' : 'Book a demo'}</Link>
              <Link to={toMarketPath('/pricing')} className="features-page-secondary">{isSpanish ? 'Ver precios' : 'View pricing'}</Link>
            </div>
          </div>

          <div className="subfeature-hero-visual">
            <div className="subfeature-hero-surface">
              <img src={heroImage.src} alt={heroImage.alt} />
            </div>
            <div className="subfeature-orbit-card subfeature-orbit-card-a landing-animate-card">
              <i className={`ph ${featureIcon}`}></i>
              <span>{page.highlights[0]}</span>
            </div>
            <div className="subfeature-orbit-card subfeature-orbit-card-b landing-animate-card">
              <i className="ph ph-check-circle"></i>
              <span>{page.highlights[1] ?? (isSpanish ? 'Flujo clínico conectado' : 'Connected clinic workflow')}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="subfeature-highlight-strip">
        <div className="features-page-shell subfeature-highlight-grid">
          {page.highlights.map((highlight, index) => (
            <article key={highlight} className="subfeature-highlight-card landing-animate-card">
              <span>{String(index + 1).padStart(2, '0')}</span>
              <p>{highlight}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="subfeature-story-section">
        <div className="features-page-shell subfeature-story-grid">
          <div className="subfeature-story-copy">
            <p className="features-page-eyebrow">{isSpanish ? 'Diseño de flujo' : 'Workflow design'}</p>
            <h2>{page.sections[0]?.title ?? (isSpanish ? 'Mantén este flujo conectado al día clínico.' : 'Keep this workflow connected to the clinic day.')}</h2>
            <p>{page.sections[0]?.body ?? page.subtitle}</p>
            <div className="features-page-check-list subfeature-check-list">
              {(page.sections[0]?.bullets ?? page.highlights).map((item) => (
                <span key={item}><i className="ph ph-check"></i>{item}</span>
              ))}
            </div>
          </div>
          <div className="subfeature-story-media landing-animate-card">
            <img src={heroImage.src} alt={heroImage.alt} loading="lazy" />
          </div>
        </div>
      </section>

      <section className="subfeature-capabilities-section">
        <div className="features-page-shell">
          <div className="features-page-section-heading">
            <p>{isSpanish ? 'Cómo ayuda Elen1' : 'How Elen1 helps'}</p>
            <h2>{isSpanish ? 'Creado para apoyar el trabajo real detrás de este flujo.' : 'Built to support the real work behind this workflow.'}</h2>
          </div>
          <div className="subfeature-capability-grid">
            {(page.capabilities?.length ? page.capabilities : page.sections).slice(0, 3).map((item, index) => (
              <article key={item.title} className="subfeature-capability-card landing-animate-card">
                <span><i className={`ph ${index === 0 ? featureIcon : index === 1 ? 'ph-chart-line-up' : 'ph-shield-check'}`}></i></span>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {page.sections[1] ? (
        <section className="subfeature-deep-section">
          <div className="features-page-shell subfeature-story-grid reverse">
            <div className="subfeature-story-copy">
              <p className="features-page-eyebrow">{isSpanish ? 'Profundidad operativa' : 'Operational depth'}</p>
              <h2>{page.sections[1].title}</h2>
              <p>{page.sections[1].body}</p>
              <div className="features-page-check-list subfeature-check-list">
                {page.sections[1].bullets.map((item) => (
                  <span key={item}><i className="ph ph-check"></i>{item}</span>
                ))}
              </div>
            </div>
            <div className="subfeature-story-media landing-animate-card">
              <img src={secondaryImage.src} alt={secondaryImage.alt} loading="lazy" />
            </div>
          </div>
        </section>
      ) : null}

      <section className="subfeature-related-section">
        <div className="features-page-shell">
          <div className="features-page-section-heading">
            <p>{isSpanish ? 'Rutas conectadas' : 'Connected paths'}</p>
            <h2>{isSpanish ? 'Explora los flujos relacionados que fortalecen este trabajo.' : 'Explore the adjacent workflows that make this stronger.'}</h2>
          </div>
          <div className="subfeature-related-grid">
            {page.related.slice(0, 4).map((relatedKey) => {
              const related = getLandingCatalogPage(relatedKey, locale);
              return (
                <Link key={relatedKey} to={toMarketPath(related.path)} className="subfeature-related-card landing-animate-card">
                  <strong>{related.title}</strong>
                  <span>{related.subtitle}</span>
                  <i className="ph ph-arrow-up-right"></i>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="features-page-demo-band">
        <div className="features-page-shell">
          <div>
            <p className="features-page-eyebrow">{isSpanish ? 'Míralo en contexto' : 'See it in context'}</p>
            <h2>{isSpanish ? `Recorre ${page.title.toLowerCase()} alrededor del flujo de tu clínica.` : `Walk through ${page.title.toLowerCase()} around your clinic workflow.`}</h2>
          </div>
          <Link to={toMarketPath('/book-demo')} className="btn-lime">{isSpanish ? 'Reservar demo de flujo' : 'Book a workflow demo'}</Link>
        </div>
      </section>
    </>
  );
}

function AboutUsPageContent({ toMarketPath, locale }: { toMarketPath: (path: string) => string; locale: LandingContentLocale }) {
  const isSpanish = locale === 'es';
  const values = isSpanish
    ? [
      { title: 'Diseñado alrededor del día clínico', body: 'Elen1 acompaña la forma en que se mueve un equipo de salud: reserva, admisión, visita, pago, seguimiento y reportes en un flujo conectado.', icon: 'ph-calendar-check' },
      { title: 'Tecnología práctica, no más administración', body: 'Cada flujo busca reducir doble captura, mostrar la próxima acción y dar a cada rol el contexto que necesita.', icon: 'ph-sparkle' },
      { title: 'Una capa operativa más tranquila', body: 'Propietarios, gerentes, profesionales y recepción comparten visibilidad sobre atención, pacientes y negocio.', icon: 'ph-heartbeat' },
    ]
    : aboutValues;
  const principles = isSpanish
    ? [
      'Hacer que las operaciones clínicas sean más fáciles de ver, asignar y mejorar.',
      'Mantener el contexto del paciente cerca de agenda, notas, facturación y seguimiento.',
      'Apoyar equipos en crecimiento sin obligarlos a usar sistemas fragmentados.',
    ]
    : aboutPrinciples;
  return (
    <>
      <section className="about-page-hero">
        <div className="about-page-shell about-page-hero-grid">
          <div className="about-page-hero-copy">
            <p className="about-page-eyebrow">{isSpanish ? 'Sobre Elen1' : 'About elen1'}</p>
            <h1>{isSpanish ? 'Creamos software clínico que mantiene conectadas la atención y la operación.' : 'We build clinic software that keeps care and operations connected.'}</h1>
            <p>
              {isSpanish
                ? 'Elen1 reúne el trabajo diario de una clínica moderna en una capa operativa clara, para que el equipo dedique menos tiempo a reconciliar sistemas y más tiempo a atender bien.'
                : 'Elen1 brings the daily work of a modern clinic into one clear operating layer, helping teams spend less time reconciling systems and more time running a steady, patient-ready practice.'}
            </p>
            <div className="about-page-actions">
              <Link to={toMarketPath('/features')} className="btn-lime">{isSpanish ? 'Explorar funciones' : 'Explore features'}</Link>
              <Link to={toMarketPath('/book-demo')} className="about-page-secondary">{isSpanish ? 'Reservar una demo' : 'Book a demo'}</Link>
            </div>
          </div>

          <div className="about-page-hero-panel landing-animate-card">
            <div className="about-page-panel-header">
              <span><i className="ph ph-buildings"></i></span>
              <strong>{isSpanish ? 'Una plataforma para el trabajo clínico' : 'One platform for clinic work'}</strong>
            </div>
            <div className="about-page-panel-screen">
              <img src="/landing-captures/dashboard-standalone.png" alt={isSpanish ? 'Panel de operaciones clínicas de Elen1' : 'Elen1 clinic operations dashboard'} />
            </div>
            <div className="about-page-proof-grid">
              <span><strong>01</strong><small>{isSpanish ? 'Historias conectadas' : 'Connected records'}</small></span>
              <span><strong>02</strong><small>{isSpanish ? 'Claridad operativa' : 'Operational clarity'}</small></span>
              <span><strong>03</strong><small>{isSpanish ? 'Seguimiento del paciente' : 'Patient follow-up'}</small></span>
            </div>
          </div>
        </div>
      </section>

      <section className="about-page-story-section">
        <div className="about-page-shell about-page-story-grid">
          <div className="about-page-story-copy">
            <p className="about-page-eyebrow">{isSpanish ? 'Por qué existimos' : 'Why we exist'}</p>
            <h2>{isSpanish ? 'Las clínicas no deberían tener que unir a mano el negocio de la atención.' : 'Clinics should not have to stitch together the business of care.'}</h2>
            <p>
              {isSpanish
                ? 'Muchos equipos superan herramientas puntuales un flujo a la vez. El resultado es doble captura, contexto del paciente disperso, responsabilidades poco claras y reportes incompletos. Elen1 reúne esas piezas en un espacio de trabajo práctico.'
                : 'Many teams outgrow point tools one workflow at a time. The result is repeated entry, scattered patient context, unclear ownership, and reporting that only tells part of the story. Elen1 is built to bring those moving parts back into one practical workspace.'}
            </p>
          </div>
          <div className="about-page-principle-list">
            {principles.map((item) => (
              <span key={item} className="landing-animate-card">
                <i className="ph ph-check"></i>
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="about-page-values-section">
        <div className="about-page-shell">
          <div className="about-page-section-heading">
            <p>{isSpanish ? 'Cómo pensamos' : 'How we think'}</p>
            <h2>{isSpanish ? 'Simple para recepción. Profundo para toda la práctica.' : 'Simple enough for the front desk. Deep enough for the whole practice.'}</h2>
          </div>
          <div className="about-page-value-grid">
            {values.map((value) => (
              <article key={value.title} className="about-page-value-card landing-animate-card">
                <span><i className={`ph ${value.icon}`}></i></span>
                <h3>{value.title}</h3>
                <p>{value.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="about-cal-section">
        <div className="about-page-shell">
          <div className="about-cal-card landing-animate-card">
            <div className="about-cal-copy">
              <p className="about-page-eyebrow">{isSpanish ? 'Conversemos' : 'Meet with us'}</p>
              <h2>{isSpanish ? 'Revisemos cómo podría verse Elen1 en tu clínica.' : 'Talk through what Elen1 could look like in your clinic.'}</h2>
              <p>
                {isSpanish
                  ? 'Elige un horario en el calendario. Usaremos la sesión para entender tu equipo, flujos, sedes y los cambios operativos que quieres priorizar.'
                  : 'Choose a time on the calendar. We will use the session to understand your team, workflows, locations, and the operational changes you want to make first.'}
              </p>
            </div>
            <div className="about-cal-embed" aria-label={isSpanish ? 'Calendario para reservar con Elen1' : 'Elen1 calendar booking'}>
              <iframe
                src={CAL_EMBED_URL}
                title={isSpanish ? 'Agenda tiempo con Elen1' : 'Schedule time with Elen1'}
                loading="lazy"
                allow="camera; microphone; fullscreen; payment"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

type PricingPlanCard = {
  name: string;
  audience: string;
  monthlyUsd: number | null;
  unit: string;
  customCadence?: string;
  cta: string;
  featured: boolean;
  points: string[];
};

function mapCatalogUnit(plan: { kind?: string; price: { unit?: string } | null }): string {
  if (!plan.price) return 'contract';
  if (plan.price.unit === 'flat') return 'contract';
  if (plan.kind === 'addon') return `${plan.price.unit ?? 'provider'} add-on`;
  return plan.price.unit ?? 'provider';
}

function mapCatalogToCards(plans: any[]): PricingPlanCard[] {
  return plans.map((p) => ({
    name: p.name,
    audience: p.tagline,
    monthlyUsd: typeof p.price?.amount === 'number' ? p.price.amount : null,
    unit: mapCatalogUnit(p),
    customCadence: p.priceCaption ?? 'tailored contract',
    cta: p.ctaLabel,
    featured: Boolean(p.recommended),
    points: Array.isArray(p.features) ? p.features : [],
  }));
}

// A pricing market entry as returned by GET /landing/plans or used as the local
// fallback. Mirrors the shape of the hardcoded `pricingMarkets` entries.
type PricingMarketEntry = {
  key: string;
  flag: string;
  label: string;
  currency: string;
  locale: string;
  monthlyPrices: Partial<Record<string, number>>;
};

function mapCatalogMarkets(markets: any[]): PricingMarketEntry[] {
  return markets
    .filter((m) => m && typeof m.key === 'string' && m.monthlyPrices && typeof m.monthlyPrices === 'object')
    .map((m) => ({
      key: String(m.key),
      flag: typeof m.flag === 'string' ? m.flag : '🌐',
      label: typeof m.label === 'string' ? m.label : String(m.key).toUpperCase(),
      currency: typeof m.currency === 'string' ? m.currency : 'USD',
      locale: typeof m.locale === 'string' ? m.locale : 'en',
      monthlyPrices: m.monthlyPrices,
    }));
}

// Pricing (plans + per-market prices) is served by the config-driven plan
// catalog (GET /landing/plans) so it can change without a deploy; the hardcoded
// lists are only a resilient fallback.
function usePricingPlans(): { plans: PricingPlanCard[]; markets: PricingMarketEntry[] } {
  const [plans, setPlans] = useState<PricingPlanCard[]>(pricingPlans);
  const [markets, setMarkets] = useState<PricingMarketEntry[]>(() => pricingMarkets as unknown as PricingMarketEntry[]);
  useEffect(() => {
    let active = true;
    fetch(`${API_BASE}/landing/plans`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!active || !data) return;
        if (Array.isArray(data.plans) && data.plans.length) {
          setPlans(mapCatalogToCards(data.plans));
        }
        if (Array.isArray(data.pricingMarkets) && data.pricingMarkets.length) {
          const mapped = mapCatalogMarkets(data.pricingMarkets);
          if (mapped.length) setMarkets(mapped);
        }
      })
      .catch(() => {
        /* keep the fallback lists */
      });
    return () => {
      active = false;
    };
  }, []);
  return { plans, markets };
}

function PricingPageContent({ toMarketPath, locale, market }: { toMarketPath: (path: string) => string; locale: LandingContentLocale; market: LandingMarket }) {
  const isSpanish = locale === 'es';
  const { plans, markets } = usePricingPlans();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('annual');
  // LATAM-first defaulting (GL-H9): Spanish / `/es` traffic defaults to the
  // generic LATAM (USD) market instead of UK GBP. Everyone else defaults to UK.
  const defaultMarketKey = market === 'es' ? 'latam' : 'uk';
  const [pricingMarketKey, setPricingMarketKey] = useState<string>(defaultMarketKey);
  const selectedMarket =
    markets.find((m) => m.key === pricingMarketKey) ??
    markets.find((m) => m.key === defaultMarketKey) ??
    markets[0];
  const isAnnual = billingCycle === 'annual';

  const formatMarketPrice = (amount: number) =>
    new Intl.NumberFormat(selectedMarket.locale, {
      style: 'currency',
      currency: selectedMarket.currency,
      maximumFractionDigits: 0,
    }).format(amount);

  const getDisplayPrice = (plan: PricingPlanCard) => {
    if (plan.monthlyUsd === null) {
      return {
        price: isSpanish ? 'A medida' : 'Custom',
        cadence: isAnnual
          ? isSpanish ? 'acuerdo anual disponible' : 'annual agreement available'
          : isSpanish
            ? plan.name === 'Multi-site' ? 'para operaciones multi-sede' : 'contrato a medida'
            : plan.customCadence,
        annualNote: '',
      };
    }

    const marketMonthlyPrice = selectedMarket.monthlyPrices[plan.name];
    const monthlyPrice = typeof marketMonthlyPrice === 'number' ? marketMonthlyPrice : plan.monthlyUsd;
    const displayMonthlyPrice = isAnnual ? Math.round(monthlyPrice * 0.8) : monthlyPrice;
    const annualTotal = Math.round(monthlyPrice * 12 * 0.8);
    const spanishUnit = plan.unit === 'provider'
      ? 'profesional'
      : plan.unit === 'provider add-on'
        ? 'profesional add-on'
        : plan.unit === 'location add-on'
          ? 'sede add-on'
          : plan.unit;

    return {
      price: formatMarketPrice(displayMonthlyPrice),
      cadence: isSpanish ? `por ${spanishUnit} / mes` : `per ${plan.unit} / month`,
      annualNote: isAnnual ? (isSpanish ? `Facturado anualmente en ${formatMarketPrice(annualTotal)}` : `Billed annually at ${formatMarketPrice(annualTotal)}`) : '',
    };
  };

  const renderCell = (value: boolean | string) => {
    if (value === true) return <span className="pricing-check"><i className="ph ph-check"></i></span>;
    if (value === false) return <span className="pricing-dash">-</span>;
    return <span className="pricing-addon-label">{value}</span>;
  };

  return (
    <>
      <section className="pricing-page-hero">
        <div className="pricing-page-shell">
          <p className="pricing-page-eyebrow">{isSpanish ? 'Precios de Elen1' : 'Elen1 pricing'}</p>
          <h1>{isSpanish ? 'Una plataforma. Planes prácticos para clínicas que quieren menos sistemas.' : 'One platform. Practical plans for clinics that want fewer systems.'}</h1>
          <p>
            {isSpanish
              ? 'Empieza con agenda, historias clínicas, pagos y operaciones. Añade IA, crecimiento, implementación y controles multi-sede cuando tu clínica necesite más profundidad.'
              : 'Start with scheduling, records, payments, and operations. Add AI, growth, implementation, and multi-site controls when your clinic needs more depth.'}
          </p>
          <div className="pricing-page-actions">
            <Link to={toMarketPath('/book-demo')} className="btn-lime">{isSpanish ? 'Reservar una demo' : 'Book a demo'}</Link>
            <Link to={toMarketPath('/features')} className="pricing-page-secondary">{isSpanish ? 'Comparar flujos' : 'Compare workflows'}</Link>
          </div>
        </div>
      </section>

      <section className="pricing-plans-section">
        <div className="pricing-page-shell">
          <div className="pricing-controls" aria-label="Pricing options">
            <div className="pricing-billing-toggle" aria-label="Choose billing cycle">
              <button
                type="button"
                className={`pricing-cycle-label ${!isAnnual ? 'is-active' : ''}`}
                onClick={() => setBillingCycle('monthly')}
              >
                {isSpanish ? 'Plan mensual' : 'Monthly plan'}
              </button>
              <button
                type="button"
                role="switch"
                aria-checked={isAnnual}
                aria-label={isSpanish ? 'Cambiar facturación anual' : 'Toggle annual billing'}
                className={`pricing-cycle-switch ${isAnnual ? 'is-annual' : ''}`}
                onClick={() => setBillingCycle(isAnnual ? 'monthly' : 'annual')}
              >
                <span></span>
              </button>
              <button
                type="button"
                className={`pricing-cycle-label ${isAnnual ? 'is-active' : ''}`}
                onClick={() => setBillingCycle('annual')}
              >
                {isSpanish ? 'Plan anual' : 'Annual plan'}
              </button>
              <span className="pricing-save-badge">{isSpanish ? 'Ahorra hasta 20%' : 'Save up to 20%'}</span>
            </div>

            <div className="pricing-market-switcher" aria-label={isSpanish ? 'Elegir mercado de precios' : 'Choose pricing market'}>
              {markets.map((marketEntry) => (
                <button
                  key={marketEntry.key}
                  type="button"
                  className={marketEntry.key === selectedMarket.key ? 'is-active' : ''}
                  onClick={() => setPricingMarketKey(marketEntry.key)}
                  aria-pressed={marketEntry.key === selectedMarket.key}
                >
                  <span aria-hidden="true">{marketEntry.flag}</span>
                  {marketEntry.label}
                </button>
              ))}
            </div>
          </div>

          <div className="pricing-plan-grid">
            {plans.map((plan) => {
              const displayPrice = getDisplayPrice(plan);

              return (
                <article key={plan.name} className={`pricing-plan-card ${plan.featured ? 'is-featured' : ''}`}>
                  <div>
                  <p className="pricing-plan-audience">{isSpanish ? ({
                    Starter: 'Clínicas individuales que se están organizando',
                    Practice: 'Equipos en crecimiento con flujo de recepción',
                    'Multi-site': 'Grupos que necesitan supervisión',
                    'Clinical AI': 'Equipos que usan flujos con IA',
                    Growth: 'Clínicas que mejoran retención',
                    Enterprise: 'Equipos complejos y despliegue a medida',
                  } as Record<string, string>)[plan.name] ?? plan.audience : plan.audience}</p>
                  <h2>{isSpanish ? ({
                    Starter: 'Inicial',
                    Practice: 'Práctica',
                    'Multi-site': 'Multi-sede',
                    'Clinical AI': 'IA clínica',
                    Growth: 'Crecimiento',
                    Enterprise: 'Empresa',
                  } as Record<string, string>)[plan.name] ?? plan.name : plan.name}</h2>
                    <div className="pricing-plan-price">
                      <strong>{displayPrice.price}</strong>
                      <span>{displayPrice.cadence}</span>
                      {displayPrice.annualNote ? <em>{displayPrice.annualNote}</em> : null}
                    </div>
                  </div>
                  <Link to={toMarketPath('/book-demo')} className="pricing-plan-cta">{isSpanish ? ({
                    Starter: 'Empezar con Inicial',
                    Practice: 'Elegir Práctica',
                    'Multi-site': 'Hablar con ventas',
                    'Clinical AI': 'Añadir IA clínica',
                    Growth: 'Añadir Crecimiento',
                    Enterprise: 'Reservar una demo',
                  } as Record<string, string>)[plan.name] ?? plan.cta : plan.cta}</Link>
                  <ul>
                    {(isSpanish ? ({
                      Starter: ['Agenda y reservas online', 'Historias de pacientes y formularios', 'Facturación y pagos básicos', 'Soporte por email'],
                      Practice: ['Todo lo de Inicial', 'Colas de equipo y coordinación de salas', 'Paquetes, membresías y depósitos', 'Reportes operativos'],
                      'Multi-site': ['Todo lo de Práctica', 'Controles multi-sede', 'Permisos avanzados y auditoría', 'Plan de implementación'],
                      'Clinical AI': ['Resúmenes de visita con IA', 'Tarjetas de contexto clínico', 'Borradores de seguimiento', 'Controles de gobierno'],
                      Growth: ['Plantillas de campañas', 'Listas de recall y reactivación', 'Solicitudes de reseñas', 'Seguimiento de referidos'],
                      Enterprise: ['Onboarding a medida', 'Soporte de seguridad y compras', 'Planificación de integraciones', 'Ruta de éxito dedicada'],
                    } as Record<string, string[]>)[plan.name] ?? plan.points : plan.points).map((point) => (
                      <li key={point}><i className="ph ph-check"></i>{point}</li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>

          <div className="pricing-included-card">
            <h2>{isSpanish ? 'Incluido en cada plan de Elen1' : 'Included with every Elen1 plan'}</h2>
            <div>
              {includedPricingItems.map((item) => (
                <span key={item.label}>
                  <i className={`ph ${item.icon}`}></i>
                  <b>{isSpanish ? ({
                    'Secure cloud hosting': 'Hosting cloud seguro',
                    'Role-based access control': 'Control de acceso por rol',
                    'Patient intake and consent forms': 'Formularios de admisión y consentimiento',
                    'Scheduling and calendar workflows': 'Agenda y flujos de calendario',
                    'Invoices, balances, and payments': 'Facturas, saldos y pagos',
                    'Operational dashboards': 'Paneles operativos',
                    'Patient messaging workflows': 'Mensajería con pacientes',
                    'Implementation checklist': 'Checklist de implementación',
                  } as Record<string, string>)[item.label] : item.label}</b>
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="pricing-addons-section">
        <div className="pricing-addons-card">
          <h2>{isSpanish ? 'Amplía tu suscripción con add-ons opcionales.' : 'Expand your subscription with optional add-ons.'}</h2>
          <p>{isSpanish ? 'Usa add-ons cuando tu despliegue necesite más soporte, integraciones más profundas o una ruta de éxito más cercana.' : 'Use add-ons when your rollout needs more support, deeper integrations, or a higher-touch success path.'}</p>
          <div className="pricing-addon-list">
            {pricingAddOns.map((addOn) => (
              <article key={addOn.name}>
                <div>
                  <strong>{isSpanish ? ({
                    'Advanced implementation': 'Implementación avanzada',
                    'Custom integrations': 'Integraciones a medida',
                    'Dedicated success': 'Éxito dedicado',
                  } as Record<string, string>)[addOn.name] : addOn.name}</strong>
                  <span>{isSpanish ? ({
                    'Advanced implementation': 'Migración, mapeo de flujos y soporte de lanzamiento para equipos complejos.',
                    'Custom integrations': 'Conecta contabilidad, marketing, analítica o sistemas locales con un plan definido.',
                    'Dedicated success': 'Revisiones trimestrales de flujo y soporte de despliegue para grupos multi-sede.',
                  } as Record<string, string>)[addOn.name] : addOn.detail}</span>
                </div>
                <b>{addOn.price}</b>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="pricing-comparison-section">
        <div className="pricing-page-shell">
          <div className="pricing-section-heading">
            <p>{isSpanish ? 'Comparar planes' : 'Compare plans'}</p>
            <h2>{isSpanish ? 'Elige el nivel de cobertura operativa que necesita tu clínica.' : 'Choose the level of operating coverage your clinic needs.'}</h2>
          </div>
          <div className="pricing-table-wrap">
            <table className="pricing-compare-table">
              <thead>
                <tr>
                  <th>{isSpanish ? 'Flujo' : 'Workflow'}</th>
                  <th>{isSpanish ? 'Inicial' : 'Starter'}</th>
                  <th>{isSpanish ? 'Práctica' : 'Practice'}</th>
                  <th>Multi-site</th>
                  <th>{isSpanish ? 'Empresa' : 'Enterprise'}</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map(([label, starter, practice, multisite, enterprise]) => (
                  <tr key={String(label)}>
                    <td>{isSpanish ? ({
                      'Online booking': 'Reserva online',
                      'Calendar and reminders': 'Calendario y recordatorios',
                      'Patient records': 'Historias de pacientes',
                      'Forms and consent': 'Formularios y consentimiento',
                      'Billing and payments': 'Facturación y pagos',
                      'Packages and memberships': 'Paquetes y membresías',
                      'Team queues and handoffs': 'Colas de equipo y traspasos',
                      'Room and resource coordination': 'Coordinación de salas y recursos',
                      'Operational reporting': 'Reportes operativos',
                      'Multi-location oversight': 'Supervisión multi-sede',
                      'Advanced permissions': 'Permisos avanzados',
                      'Audit and governance support': 'Soporte de auditoría y gobierno',
                      'Clinical AI workflows': 'Flujos de IA clínica',
                      'Growth campaigns': 'Campañas de crecimiento',
                      'Custom implementation plan': 'Plan de implementación a medida',
                    } as Record<string, string>)[String(label)] : label}</td>
                    <td>{renderCell(starter)}</td>
                    <td>{renderCell(practice)}</td>
                    <td>{renderCell(multisite)}</td>
                    <td>{renderCell(enterprise)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="pricing-questions-section">
        <div className="pricing-page-shell pricing-questions-grid">
          <div>
            <p className="pricing-page-eyebrow">{isSpanish ? 'Preguntas sobre precios' : 'Pricing questions'}</p>
            <h2>{isSpanish ? '¿Necesitas ayuda para elegir el punto de partida correcto?' : 'Need help choosing the right starting point?'}</h2>
            <p>{isSpanish ? 'Podemos mapear el tamaño de tu clínica, sedes, flujos y necesidades de despliegue antes de elegir un plan.' : 'We can map your clinic size, locations, workflows, and rollout needs before you commit to a plan.'}</p>
            <Link to={toMarketPath('/book-demo')} className="pricing-plan-cta">{isSpanish ? 'Reservar recorrido de precios' : 'Book a pricing walkthrough'}</Link>
          </div>
          <div className="pricing-question-list">
            {(isSpanish ? [
              { question: '¿Podemos empezar pequeño y añadir más después?', answer: 'Sí. La mayoría de equipos empieza con los flujos que necesita ahora y luego expande IA, crecimiento, reportes o controles multi-sede.' },
              { question: '¿La implementación está incluida?', answer: 'Cada plan incluye un checklist de lanzamiento. Clínicas más grandes o complejas pueden añadir soporte de implementación definido.' },
              { question: '¿Los precios incluyen comisiones de procesamiento de pagos?', answer: 'El precio del software es independiente de comisiones de tarjetas, mensajería y servicios de terceros cuando apliquen.' },
              { question: '¿Ofrecen contratos a medida?', answer: 'Sí. Despliegues multi-sede, empresariales o con integraciones pueden cotizarse según alcance y necesidades de compra.' },
            ] : pricingQuestions).map((item) => (
              <details key={item.question}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function BookDemoPageContent({ locale }: { locale: LandingContentLocale }) {
  const isSpanish = locale === 'es';
  return (
    <section className="book-demo-simple-page">
      <div className="book-demo-simple-shell">
        <div className="book-demo-simple-heading">
          <p>{isSpanish ? 'Reservar una demo' : 'Book a demo'}</p>
          <h1>{isSpanish ? 'Elige un horario que te funcione.' : 'Choose a time that works for you.'}</h1>
        </div>
        <div className="book-demo-simple-calendar">
          <iframe
            src={CAL_EMBED_URL}
            title={isSpanish ? 'Agenda una demo de Elen1' : 'Schedule an Elen1 demo'}
            loading="lazy"
            allow="camera; microphone; fullscreen; payment"
          ></iframe>
        </div>
      </div>
    </section>
  );
}

function WellnessPreventiveCarePageContent({ toMarketPath }: { toMarketPath: (path: string) => string }) {
  const focusAreas = ['Health plans', 'Screening recalls', 'Lifestyle programs', 'Follow-up tasks', 'Patient goals', 'Outcome tracking'];
  const stats = [
    { value: '1.6x', label: 'more reliable follow-through when care plans and reminders stay connected' },
    { value: '42%', label: 'less manual coordination across recalls, intake, tasks, and check-ins' },
    { value: '24/7', label: 'patient access to booking, forms, visit details, and next steps' },
  ];
  const productCards = [
    { title: 'Preventive care tracks', body: 'Create structured plans for screenings, lifestyle visits, annual reviews, and ongoing follow-up.' },
    { title: 'Between-visit continuity', body: 'Keep reminders, goals, tasks, forms, messages, and next steps visible after the appointment ends.' },
    { title: 'Engagement visibility', body: 'Track missed recalls, upcoming reviews, program participation, and patient activity from one reporting layer.' },
    { title: 'Team accountability', body: 'Give providers, coordinators, and front desk staff a shared view of open preventive-care work.' },
  ];
  const faqs = [
    ['Can Elen1 support preventive care programs?', 'Yes. Elen1 can help teams manage recurring reviews, recalls, lifestyle programs, patient tasks, reminders, and follow-up in one workflow.'],
    ['Does this work for longitudinal wellness models?', 'Yes. The workflow is designed for clinics where continuity, patient goals, and proactive follow-through matter between appointments.'],
    ['Can patients prepare before the visit?', 'Yes. Patients can complete booking, intake, forms, reminders, and visit preparation before they arrive.'],
    ['Can managers track whether patients are staying engaged?', 'Yes. Reporting connects appointments, recalls, patient activity, follow-up tasks, and program participation.'],
  ];

  return (
    <main className="wellness-page">
      <section className="wellness-hero">
        <div className="wellness-shell wellness-hero-grid">
          <div className="wellness-hero-copy">
            <p className="wellness-eyebrow">Wellness and preventive care</p>
            <h1>Keep preventive care moving before, during, and after every visit.</h1>
            <p>
              Elen1 helps wellness teams coordinate recalls, screenings, lifestyle programs, intake, care goals, reminders, and follow-up from one connected operating layer.
            </p>
            <div className="wellness-actions">
              <Link to={toMarketPath('/book-demo')} className="btn-lime">Book a demo</Link>
              <Link to={toMarketPath('/pricing')} className="wellness-secondary">View pricing</Link>
            </div>
            <div className="wellness-proof-row">
              {['Screening recalls', 'Lifestyle programs', 'Ongoing follow-up'].map((item) => (
                <span key={item}><i className="ph ph-check-circle"></i>{item}</span>
              ))}
            </div>
          </div>
          <div className="wellness-hero-visual">
            <div className="wellness-phone-card">
              <img src="/landing-generated/growth-insights-clinic.png" alt="Wellness clinic team with patient engagement workflow" />
            </div>
            <div className="wellness-dashboard-card wellness-dashboard-card-a">
              <strong>10+</strong>
              <span>program types managed in one workflow</span>
            </div>
            <div className="wellness-dashboard-card wellness-dashboard-card-b">
              <img src="/landing-captures/scheduling-standalone.png" alt="Elen1 scheduling workflow preview" />
            </div>
          </div>
        </div>
      </section>

      <section className="wellness-logo-band">
        <div className="wellness-shell">
          <p>Built for preventive models that depend on continuity</p>
          <div>
            {focusAreas.map((item) => <span key={item}>{item}</span>)}
          </div>
        </div>
      </section>

      <section className="wellness-compare">
        <div className="wellness-shell wellness-section-heading">
          <p className="wellness-eyebrow">Workflow clarity</p>
          <h2>Preventive care breaks down when next steps live in too many places.</h2>
        </div>
        <div className="wellness-shell wellness-compare-grid">
          <div className="wellness-check-card">
            <h3>Without Elen1</h3>
            {['Screening recalls tracked outside the schedule', 'Forms and history updates chased manually', 'Patient goals disconnected from follow-up tasks', 'Next reviews depend on staff memory'].map((item) => (
              <span key={item}><i className="ph ph-x"></i>{item}</span>
            ))}
          </div>
          <div className="wellness-check-card is-green">
            <h3>With Elen1</h3>
            {['Recalls, visits, reminders, and goals stay connected', 'Patients complete intake before arrival', 'Care-plan tasks remain visible between visits', 'Follow-up work moves with the patient record'].map((item) => (
              <span key={item}><i className="ph ph-check"></i>{item}</span>
            ))}
            <Link to={toMarketPath('/book-demo')}>See the workflow <i className="ph ph-arrow-up-right"></i></Link>
          </div>
        </div>
      </section>

      <section className="wellness-feature-block">
        <div className="wellness-shell wellness-feature-grid">
          <div className="wellness-feature-copy">
            <p className="wellness-eyebrow">Preventive care tracks</p>
            <h2>Turn proactive care into a repeatable clinic workflow.</h2>
            <p>Run screenings, reviews, lifestyle plans, reminders, and follow-up tasks with patient context visible to the team.</p>
            {['Create structured recall pathways', 'Keep patient goals visible to the team', 'Reduce manual coordination between check-ins'].map((item) => (
              <details key={item} open={item === 'Create structured recall pathways'}>
                <summary>{item}</summary>
                <p>Use Elen1 to make preventive care easier to repeat without turning patient relationships into a generic checklist.</p>
              </details>
            ))}
          </div>
          <div className="wellness-feature-media">
            <img src="/landing-captures/patients-standalone.png" alt="Patient workflow and care plan preview" />
          </div>
        </div>
      </section>

      <section className="wellness-feature-block wellness-feature-block-alt">
        <div className="wellness-shell wellness-feature-grid reverse">
          <div className="wellness-feature-copy">
            <p className="wellness-eyebrow">Patient access</p>
            <h2>Make it easier for patients to stay on track.</h2>
            <p>Give patients a clearer path to book, prepare, complete forms, understand next steps, and return for preventive follow-up.</p>
            {['Online booking tied to visit type and provider', 'Forms and preparation sent before the visit', 'Reminders and recalls support continuity'].map((item) => (
              <details key={item} open={item === 'Online booking tied to visit type and provider'}>
                <summary>{item}</summary>
                <p>Keep access and preparation close to the appointment so patients arrive ready and know what happens next.</p>
              </details>
            ))}
          </div>
          <div className="wellness-feature-media stacked">
            <img src="/landing-captures/billing-standalone.png" alt="Billing and payment workflow preview" />
            <img src="/landing-captures/scheduling-standalone.png" alt="Calendar workflow preview" />
          </div>
        </div>
      </section>

      <section className="wellness-stats-band">
        <div className="wellness-shell">
          <div className="wellness-stats-copy">
            <p className="wellness-eyebrow">Care continuity</p>
            <h2>Less time chasing tasks means more time keeping patients engaged.</h2>
          </div>
          <div className="wellness-stat-grid">
            {stats.map((stat) => (
              <article key={stat.value}>
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </article>
            ))}
            <article className="wellness-stat-photo">
              <img src="/landing-generated/operations-team-clinic.png" alt="Clinic team reviewing operations" />
            </article>
          </div>
        </div>
      </section>

      <section className="wellness-feature-block">
        <div className="wellness-shell wellness-feature-grid">
          <div className="wellness-feature-copy">
            <p className="wellness-eyebrow">Team coordination</p>
            <h2>Give every role a clearer view of what needs follow-through.</h2>
            <p>Help providers, coordinators, and front desk teams see patient status, overdue recalls, open tasks, and next actions in one place.</p>
            {['Reduce side messages between roles', 'Keep preventive tasks tied to the patient', 'Spot missed follow-up before it becomes a gap'].map((item) => (
              <details key={item} open={item === 'Reduce side messages between roles'}>
                <summary>{item}</summary>
                <p>Everyone works from the same preventive-care picture, with role-based access for the work they own.</p>
              </details>
            ))}
          </div>
          <div className="wellness-feature-media">
            <img src="/landing-captures/staff-operations-standalone.png" alt="Staff operations workflow preview" />
          </div>
        </div>
      </section>

      <section className="wellness-product-section">
        <div className="wellness-shell">
          <div className="wellness-section-heading">
            <p className="wellness-eyebrow">Product depth</p>
            <h2>Preventive care software made more connected.</h2>
          </div>
          <div className="wellness-product-board">
            {productCards.map((card) => (
              <article key={card.title}>
                <img src="/landing-captures/dashboard-standalone.png" alt="" aria-hidden="true" />
                <h3>{card.title}</h3>
                <p>{card.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="wellness-explore-section">
        <div className="wellness-shell">
          <h2>Explore Elen1 by practice size</h2>
          <div className="wellness-size-grid">
            {[
              ['Solo practitioner', '/solo-practitioner'],
              ['Small practice', '/small-practice'],
              ['Large practice', '/large-practice'],
              ['Enterprise', '/enterprise'],
            ].map(([label, path]) => (
              <Link key={label} to={toMarketPath(path)}>{label}<i className="ph ph-arrow-up-right"></i></Link>
            ))}
          </div>
        </div>
      </section>

      <section className="wellness-faq-section">
        <div className="wellness-shell">
          <div className="wellness-section-heading">
            <p className="wellness-eyebrow">Questions</p>
            <h2>Frequently asked questions</h2>
          </div>
          <div className="wellness-faq-list">
            {faqs.map(([question, answer]) => (
              <details key={question}>
                <summary>{question}</summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="wellness-cta-band">
        <div className="wellness-shell wellness-cta-card">
          <div>
            <p className="wellness-eyebrow">Elen1 for preventive care</p>
            <h2>Support healthier patient habits with a clearer operating workflow.</h2>
            <p>See how the platform can support recalls, screenings, lifestyle programs, patient goals, team coordination, and long-term engagement.</p>
            <div className="wellness-actions">
              <Link to={toMarketPath('/book-demo')} className="btn-lime">Book a demo</Link>
              <Link to={toMarketPath('/features')} className="wellness-secondary dark">Explore features</Link>
            </div>
          </div>
          <img src="/landing-captures/reports-standalone.png" alt="Elen1 reporting dashboard preview" />
        </div>
      </section>
    </main>
  );
}

function MedspaPageContent({ toMarketPath }: { toMarketPath: (path: string) => string }) {
  const focusAreas = ['Consultations', 'Treatment plans', 'Packages', 'Deposits', 'Memberships', 'Aftercare'];
  const stats = [
    { value: '1.8x', label: 'more consistent rebooking when treatment plans stay visible after checkout' },
    { value: '38%', label: 'less front-desk friction across consults, deposits, forms, and invoices' },
    { value: '24/7', label: 'patient self-service for booking, intake, visit details, and follow-up' },
  ];
  const productCards = [
    { title: 'Consult management', body: 'Move from inquiry to consultation, quote, consent, deposit, and booked treatment without losing context.' },
    { title: 'Treatment journeys', body: 'Keep plans, photos, forms, aftercare tasks, and return visits tied to the same patient record.' },
    { title: 'Packages and payments', body: 'Track deposits, balances, packages, memberships, and invoices close to the appointment workflow.' },
    { title: 'Retention workflows', body: 'Use reminders, rebooking prompts, recalls, and campaigns to bring patients back at the right time.' },
  ];
  const faqs = [
    ['Can Elen1 support packages and memberships for a medspa?', 'Yes. Elen1 can help manage packages, memberships, deposits, balances, invoices, and repeat bookings in one connected workflow.'],
    ['Does this work for consult-to-treatment workflows?', 'Yes. The page is built around medspa workflows where consults, forms, consent, payment, treatment planning, and aftercare need to stay connected.'],
    ['Can patients complete forms and consent before arrival?', 'Yes. Intake, consent, appointment preparation, and reminders can be sent before the patient arrives.'],
    ['Can managers see revenue and retention performance?', 'Yes. Reporting connects appointments, payments, packages, utilization, patient activity, and follow-up signals.'],
  ];

  return (
    <main className="wellness-page medspa-page">
      <section className="wellness-hero">
        <div className="wellness-shell wellness-hero-grid">
          <div className="wellness-hero-copy">
            <p className="wellness-eyebrow">Medspa software</p>
            <h1>Run a polished medspa experience from consult to repeat visit.</h1>
            <p>
              Elen1 connects consultations, digital forms, treatment plans, deposits, packages, payments, photos, reminders, and aftercare so your team can deliver a smoother premium experience with less admin work.
            </p>
            <div className="wellness-actions">
              <Link to={toMarketPath('/book-demo')} className="btn-lime">Book a demo</Link>
              <Link to={toMarketPath('/pricing')} className="wellness-secondary">View pricing</Link>
            </div>
            <div className="wellness-proof-row">
              {['Consult-to-treatment flow', 'Packages and deposits', 'Rebooking and aftercare'].map((item) => (
                <span key={item}><i className="ph ph-check-circle"></i>{item}</span>
              ))}
            </div>
          </div>
          <div className="wellness-hero-visual">
            <div className="wellness-phone-card">
              <img src="/landing-generated/operations-team-clinic.png" alt="Medspa team reviewing patient and treatment workflow" />
            </div>
            <div className="wellness-dashboard-card wellness-dashboard-card-a">
              <strong>12+</strong>
              <span>treatment and package workflows managed together</span>
            </div>
            <div className="wellness-dashboard-card wellness-dashboard-card-b">
              <img src="/landing-captures/billing-standalone.png" alt="Elen1 medspa billing and package workflow preview" />
            </div>
          </div>
        </div>
      </section>

      <section className="wellness-logo-band">
        <div className="wellness-shell">
          <p>Built for medspas that sell outcomes, not just appointments</p>
          <div>
            {focusAreas.map((item) => <span key={item}>{item}</span>)}
          </div>
        </div>
      </section>

      <section className="wellness-compare">
        <div className="wellness-shell wellness-section-heading">
          <p className="wellness-eyebrow">Operational clarity</p>
          <h2>When demand grows, disconnected tools make the patient experience harder to protect.</h2>
        </div>
        <div className="wellness-shell wellness-compare-grid">
          <div className="wellness-check-card">
            <h3>Without Elen1</h3>
            {['Consult notes sit apart from booking and payment', 'Consent and forms are chased manually', 'Packages and balances are tracked in separate places', 'Aftercare and rebooking depend on staff memory'].map((item) => (
              <span key={item}><i className="ph ph-x"></i>{item}</span>
            ))}
          </div>
          <div className="wellness-check-card is-green">
            <h3>With Elen1</h3>
            {['Consults, forms, quotes, deposits, and visits stay connected', 'Patients complete prep before they arrive', 'Packages, memberships, and balances stay visible at checkout', 'Aftercare tasks and rebooking prompts follow the patient'].map((item) => (
              <span key={item}><i className="ph ph-check"></i>{item}</span>
            ))}
            <Link to={toMarketPath('/book-demo')}>See the medspa workflow <i className="ph ph-arrow-up-right"></i></Link>
          </div>
        </div>
      </section>

      <section className="wellness-feature-block">
        <div className="wellness-shell wellness-feature-grid">
          <div className="wellness-feature-copy">
            <p className="wellness-eyebrow">Consults and treatment plans</p>
            <h2>Keep every treatment journey organized from the first conversation.</h2>
            <p>Give your team one place to manage consultation details, treatment recommendations, before-and-after context, consent, and follow-up.</p>
            {['Create a repeatable consult workflow', 'Keep treatment context visible across visits', 'Move from recommendation to booked service faster'].map((item) => (
              <details key={item} open={item === 'Create a repeatable consult workflow'}>
                <summary>{item}</summary>
                <p>Use Elen1 to standardize the operational flow while preserving the personal, high-touch medspa experience patients expect.</p>
              </details>
            ))}
          </div>
          <div className="wellness-feature-media">
            <img src="/landing-captures/patients-standalone.png" alt="Medspa patient treatment workflow preview" />
          </div>
        </div>
      </section>

      <section className="wellness-feature-block wellness-feature-block-alt">
        <div className="wellness-shell wellness-feature-grid reverse">
          <div className="wellness-feature-copy">
            <p className="wellness-eyebrow">Packages and payments</p>
            <h2>Sell packages, collect deposits, and rebook with less friction.</h2>
            <p>Keep invoices, deposits, memberships, package balances, and next appointments close to the visit so front desk work feels cleaner.</p>
            {['Deposits tied to appointments and treatments', 'Package balances visible when patients check out', 'Waitlist and reminders help protect utilization'].map((item) => (
              <details key={item} open={item === 'Deposits tied to appointments and treatments'}>
                <summary>{item}</summary>
                <p>Keep revenue steps connected to the appointment so your team is not reconciling separate payment and scheduling systems.</p>
              </details>
            ))}
          </div>
          <div className="wellness-feature-media stacked">
            <img src="/landing-captures/billing-standalone.png" alt="Medspa billing and payment workflow preview" />
            <img src="/landing-captures/scheduling-standalone.png" alt="Medspa calendar and booking workflow preview" />
          </div>
        </div>
      </section>

      <section className="wellness-stats-band">
        <div className="wellness-shell">
          <div className="wellness-stats-copy">
            <p className="wellness-eyebrow">Retention and revenue</p>
            <h2>Time saved on admin becomes time spent improving patient experience.</h2>
          </div>
          <div className="wellness-stat-grid">
            {stats.map((stat) => (
              <article key={stat.value}>
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </article>
            ))}
            <article className="wellness-stat-photo">
              <img src="/landing-generated/growth-insights-clinic.png" alt="Medspa growth and patient engagement workflow" />
            </article>
          </div>
        </div>
      </section>

      <section className="wellness-feature-block">
        <div className="wellness-shell wellness-feature-grid">
          <div className="wellness-feature-copy">
            <p className="wellness-eyebrow">Team coordination</p>
            <h2>Give providers, front desk, and managers the same operating picture.</h2>
            <p>Coordinate treatment rooms, providers, forms, payments, retail, aftercare, and follow-up without relying on side messages.</p>
            {['Reduce handoff gaps between roles', 'Keep next steps tied to the patient', 'Spot open balances and missed follow-up earlier'].map((item) => (
              <details key={item} open={item === 'Reduce handoff gaps between roles'}>
                <summary>{item}</summary>
                <p>Everyone works from the same medspa workflow, with role-based visibility for the tasks they own.</p>
              </details>
            ))}
          </div>
          <div className="wellness-feature-media">
            <img src="/landing-captures/staff-operations-standalone.png" alt="Medspa staff operations workflow preview" />
          </div>
        </div>
      </section>

      <section className="wellness-product-section">
        <div className="wellness-shell">
          <div className="wellness-section-heading">
            <p className="wellness-eyebrow">Product depth</p>
            <h2>Medspa software made more connected.</h2>
          </div>
          <div className="wellness-product-board">
            {productCards.map((card) => (
              <article key={card.title}>
                <img src="/landing-captures/dashboard-standalone.png" alt="" aria-hidden="true" />
                <h3>{card.title}</h3>
                <p>{card.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="wellness-explore-section">
        <div className="wellness-shell">
          <h2>Explore Elen1 by practice size</h2>
          <div className="wellness-size-grid">
            {[
              ['Solo practitioner', '/solo-practitioner'],
              ['Small practice', '/small-practice'],
              ['Large practice', '/large-practice'],
              ['Enterprise', '/enterprise'],
            ].map(([label, path]) => (
              <Link key={label} to={toMarketPath(path)}>{label}<i className="ph ph-arrow-up-right"></i></Link>
            ))}
          </div>
        </div>
      </section>

      <section className="wellness-faq-section">
        <div className="wellness-shell">
          <div className="wellness-section-heading">
            <p className="wellness-eyebrow">Questions</p>
            <h2>Frequently asked questions</h2>
          </div>
          <div className="wellness-faq-list">
            {faqs.map(([question, answer]) => (
              <details key={question}>
                <summary>{question}</summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="wellness-cta-band">
        <div className="wellness-shell wellness-cta-card">
          <div>
            <p className="wellness-eyebrow">Elen1 for medspas</p>
            <h2>Deliver a smoother premium experience from consult to aftercare.</h2>
            <p>See how Elen1 can support your medspa services, treatment journeys, packages, revenue workflow, and patient retention strategy.</p>
            <div className="wellness-actions">
              <Link to={toMarketPath('/book-demo')} className="btn-lime">Book a demo</Link>
              <Link to={toMarketPath('/features')} className="wellness-secondary dark">Explore features</Link>
            </div>
          </div>
          <img src="/landing-captures/reports-standalone.png" alt="Elen1 medspa reporting dashboard preview" />
        </div>
      </section>
    </main>
  );
}

function SolutionLandingPageContent({
  page,
  pageKey,
  toMarketPath,
  locale,
}: {
  page: ReturnType<typeof getLandingCatalogPage>;
  pageKey: LandingCatalogPageKey;
  toMarketPath: (path: string) => string;
  locale: LandingContentLocale;
}) {
  const isSpanish = locale === 'es';
  const titleLabel = page.title.replace(/^Elen1 for\s+/i, '').replace(/^Elen1 para\s+/i, '').replace(/\.$/, '');
  const isRolePage = ['clinic-owners', 'practice-managers', 'practitioners', 'practice-owner', 'treatment-providers', 'front-of-house-staff', 'marketers'].includes(pageKey);
  const isBusinessSizePage = ['solo-practices', 'growing-teams', 'multi-location-groups', 'enterprise', 'large-practice', 'small-practice', 'solo-practitioner'].includes(pageKey);
  const visualA = isRolePage
    ? '/landing-captures/staff-operations-standalone.png'
    : isBusinessSizePage
      ? '/landing-captures/dashboard-standalone.png'
      : '/landing-captures/patients-standalone.png';
  const visualB = isRolePage
    ? '/landing-captures/dashboard-standalone.png'
    : isBusinessSizePage
      ? '/landing-captures/reports-standalone.png'
      : '/landing-captures/scheduling-standalone.png';
  const heroPhoto = isRolePage
    ? '/landing-generated/operations-team-clinic.png'
    : isBusinessSizePage
      ? '/landing-generated/who-we-serve-hero.png'
      : '/landing-generated/growth-insights-clinic.png';
  const focusAreas = [
    ...page.highlights,
    ...(page.sections[0]?.bullets ?? []),
  ].slice(0, 6);
  const productCards = (page.capabilities?.length ? page.capabilities : page.sections).slice(0, 4);
  const faqs = page.faq?.slice(0, 4) ?? [
    { question: isSpanish ? `¿Cómo apoya Elen1 a ${titleLabel.toLowerCase()}?` : `How does Elen1 support ${titleLabel.toLowerCase()}?`, answer: page.subtitle },
    { question: isSpanish ? '¿Qué flujos están conectados?' : 'What workflows are connected?', answer: isSpanish ? 'Agenda, admisión, documentación, facturación, comunicación, reportes y seguimiento pueden mantenerse conectados en un solo sistema.' : 'Scheduling, intake, documentation, billing, communication, reporting, and follow-up can stay connected in one system.' },
    { question: isSpanish ? '¿Puede escalar con nuestro equipo?' : 'Can this scale with our team?', answer: isSpanish ? 'Sí. Elen1 soporta flujos por rol, reportes, permisos y visibilidad operativa a medida que crecen equipos y sedes.' : 'Yes. Elen1 supports role-based workflows, reporting, permissions, and operational visibility as teams and locations grow.' },
    { question: isSpanish ? '¿Podemos verlo en una demo?' : 'Can we see this in a demo?', answer: isSpanish ? 'Sí. La demo puede adaptarse a tu especialidad, rol, tamaño de negocio y brechas actuales de flujo.' : 'Yes. A demo can be tailored around your specialty, role, business size, and current workflow gaps.' },
  ];
  const stats = [
    { value: '1.5x', label: isSpanish ? 'seguimiento más consistente cuando pacientes, equipo e ingresos permanecen conectados' : 'more consistent follow-through when patient, team, and revenue workflows stay connected' },
    { value: '36%', label: isSpanish ? 'menos coordinación manual entre reserva, admisión, traspasos y seguimiento' : 'less manual coordination across booking, intake, handoffs, and follow-up' },
    { value: '24/7', label: isSpanish ? 'autoservicio del paciente para reservas, formularios, detalles de visita y próximos pasos' : 'patient self-service for booking, forms, visit details, and next steps' },
  ];
  const proofItems = focusAreas.slice(0, 3);
  const firstSection = page.sections[0] ?? {
    title: isSpanish ? `Haz que ${titleLabel.toLowerCase()} sea más fácil de operar día a día.` : `Make ${titleLabel.toLowerCase()} easier to run day to day.`,
    body: page.subtitle,
    bullets: page.highlights,
  };
  const secondSection = page.sections[1] ?? {
    title: isSpanish ? 'Mantén visible el siguiente paso después de cada visita.' : 'Keep the next step visible after every visit.',
    body: isSpanish ? 'Elen1 mantiene tareas, comunicación, pagos y reportes cerca del flujo para que el equipo avance con menos seguimiento manual.' : 'Elen1 keeps tasks, communication, payments, and reporting close to the workflow so teams can keep moving with less manual follow-up.',
    bullets: page.highlights,
  };

  return (
    <main className="wellness-page solution-template-page">
      <section className="wellness-hero">
        <div className="wellness-shell wellness-hero-grid">
          <div className="wellness-hero-copy">
            <p className="wellness-eyebrow">{page.eyebrow}</p>
            <h1>{page.title}</h1>
            <p>{page.subtitle}</p>
            <div className="wellness-actions">
              <Link to={toMarketPath('/book-demo')} className="btn-lime">{isSpanish ? 'Reservar una demo' : 'Book a demo'}</Link>
              <Link to={toMarketPath('/pricing')} className="wellness-secondary">{isSpanish ? 'Ver precios' : 'View pricing'}</Link>
            </div>
            <div className="wellness-proof-row">
              {proofItems.map((item) => (
                <span key={item}><i className="ph ph-check-circle"></i>{item}</span>
              ))}
            </div>
          </div>
          <div className="wellness-hero-visual">
            <div className="wellness-phone-card">
              <img src={heroPhoto} alt={isSpanish ? `Visual de flujo para ${titleLabel}` : `${titleLabel} workflow visual`} />
            </div>
            <div className="wellness-dashboard-card wellness-dashboard-card-a">
              <strong>{isBusinessSizePage ? 'Multi' : isRolePage ? (isSpanish ? 'Rol' : 'Role') : (isSpanish ? 'Atención' : 'Care')}</strong>
              <span>{isBusinessSizePage ? (isSpanish ? 'modelos operativos soportados' : 'operating models supported') : isRolePage ? (isSpanish ? 'flujos específicos alineados' : 'specific workflows aligned') : (isSpanish ? 'recorridos conectados de principio a fin' : 'journeys connected end to end')}</span>
            </div>
            <div className="wellness-dashboard-card wellness-dashboard-card-b">
              <img src={visualB} alt={isSpanish ? `Vista de producto para ${titleLabel}` : `${titleLabel} product preview`} />
            </div>
          </div>
        </div>
      </section>

      <section className="wellness-logo-band">
        <div className="wellness-shell">
          <p>{isSpanish ? 'Creado para equipos que necesitan ejecución conectada' : 'Built for teams that need connected execution'}</p>
          <div>
            {focusAreas.map((item) => <span key={item}>{item}</span>)}
          </div>
        </div>
      </section>

      <section className="wellness-compare">
        <div className="wellness-shell wellness-section-heading">
          <p className="wellness-eyebrow">{isSpanish ? 'Claridad de flujo' : 'Workflow clarity'}</p>
          <h2>{firstSection.title}</h2>
        </div>
        <div className="wellness-shell wellness-compare-grid">
          <div className="wellness-check-card">
            <h3>{isSpanish ? 'Sin Elen1' : 'Without Elen1'}</h3>
            {(isSpanish
              ? [
                'Contexto del paciente y operación dividido entre herramientas',
                'El seguimiento depende de mensajes, hojas de cálculo o memoria',
                'Ingresos, utilización y carga de trabajo son difíciles de revisar en tiempo real',
                'El equipo pierde tiempo reconciliando el mismo trabajo en varios lugares',
              ]
              : [
                'Patient and operational context split across disconnected tools',
                'Follow-up depends on side messages, spreadsheets, or memory',
                'Revenue, utilization, and workload are hard to review in real time',
                'Teams spend extra time reconciling the same work in multiple places',
              ]).map((item) => (
              <span key={item}><i className="ph ph-x"></i>{item}</span>
            ))}
          </div>
          <div className="wellness-check-card is-green">
            <h3>{isSpanish ? 'Con Elen1' : 'With Elen1'}</h3>
            {firstSection.bullets.map((item) => (
              <span key={item}><i className="ph ph-check"></i>{item}</span>
            ))}
            <Link to={toMarketPath('/book-demo')}>{isSpanish ? 'Ver el flujo' : 'See the workflow'} <i className="ph ph-arrow-up-right"></i></Link>
          </div>
        </div>
      </section>

      <section className="wellness-feature-block">
        <div className="wellness-shell wellness-feature-grid">
          <div className="wellness-feature-copy">
            <p className="wellness-eyebrow">{isSpanish ? 'Flujo diario' : 'Daily workflow'}</p>
            <h2>{firstSection.title}</h2>
            <p>{firstSection.body}</p>
            {firstSection.bullets.map((item, index) => (
              <details key={item} open={index === 0}>
                <summary>{item}</summary>
                <p>{isSpanish ? 'Usa Elen1 para mantener este trabajo cerca del contexto del paciente, equipo, pago y reporte.' : 'Use Elen1 to keep this work close to the patient, team, payment, and reporting context around it.'}</p>
              </details>
            ))}
          </div>
          <div className="wellness-feature-media">
            <img src={visualA} alt={isSpanish ? `Pantalla de flujo para ${titleLabel}` : `${titleLabel} workflow screen`} />
          </div>
        </div>
      </section>

      <section className="wellness-feature-block wellness-feature-block-alt">
        <div className="wellness-shell wellness-feature-grid reverse">
          <div className="wellness-feature-copy">
            <p className="wellness-eyebrow">{isSpanish ? 'Profundidad operativa' : 'Operational depth'}</p>
            <h2>{secondSection.title}</h2>
            <p>{secondSection.body}</p>
            {secondSection.bullets.map((item, index) => (
              <details key={item} open={index === 0}>
                <summary>{item}</summary>
                <p>{isSpanish ? 'Mantén clara la próxima acción para que el equipo avance más rápido sin perder responsabilidad.' : 'Keep the next action clear so teams can move faster without losing accountability.'}</p>
              </details>
            ))}
          </div>
          <div className="wellness-feature-media stacked">
            <img src="/landing-captures/billing-standalone.png" alt={isSpanish ? `Flujo de facturación para ${titleLabel}` : `${titleLabel} billing workflow`} />
            <img src="/landing-captures/scheduling-standalone.png" alt={isSpanish ? `Flujo de agenda para ${titleLabel}` : `${titleLabel} scheduling workflow`} />
          </div>
        </div>
      </section>

      <section className="wellness-stats-band">
        <div className="wellness-shell">
          <div className="wellness-stats-copy">
            <p className="wellness-eyebrow">{isSpanish ? 'Impacto operativo' : 'Operating impact'}</p>
            <h2>{isSpanish ? 'Menos tiempo reconciliando trabajo significa más tiempo mejorando la experiencia del paciente.' : 'Less time reconciling work means more time improving the patient experience.'}</h2>
          </div>
          <div className="wellness-stat-grid">
            {stats.map((stat) => (
              <article key={stat.value}>
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </article>
            ))}
            <article className="wellness-stat-photo">
              <img src="/landing-generated/operations-team-clinic.png" alt={isSpanish ? `Flujo de equipo para ${titleLabel}` : `${titleLabel} team workflow`} />
            </article>
          </div>
        </div>
      </section>

      <section className="wellness-product-section">
        <div className="wellness-shell">
          <div className="wellness-section-heading">
            <p className="wellness-eyebrow">{isSpanish ? 'Profundidad del producto' : 'Product depth'}</p>
            <h2>{isSpanish ? `Flujos de ${titleLabel} más conectados.` : `${titleLabel} workflows made more connected.`}</h2>
          </div>
          <div className="wellness-product-board">
            {productCards.map((card) => (
              <article key={card.title}>
                <img src="/landing-captures/dashboard-standalone.png" alt="" aria-hidden="true" />
                <h3>{card.title}</h3>
                <p>{card.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="wellness-explore-section">
        <div className="wellness-shell">
          <h2>{isSpanish ? 'Explora rutas conectadas de Elen1' : 'Explore connected Elen1 paths'}</h2>
          <div className="wellness-size-grid">
            {page.related.slice(0, 4).map((relatedKey) => {
              const related = getLandingCatalogPage(relatedKey, locale);
              return (
                <Link key={relatedKey} to={toMarketPath(related.path)}>{related.eyebrow}<i className="ph ph-arrow-up-right"></i></Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="wellness-faq-section">
        <div className="wellness-shell">
          <div className="wellness-section-heading">
            <p className="wellness-eyebrow">{isSpanish ? 'Preguntas' : 'Questions'}</p>
            <h2>{isSpanish ? 'Preguntas frecuentes' : 'Frequently asked questions'}</h2>
          </div>
          <div className="wellness-faq-list">
            {faqs.map((item) => (
              <details key={item.question}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="wellness-cta-band">
        <div className="wellness-shell wellness-cta-card">
          <div>
            <p className="wellness-eyebrow">{isSpanish ? `Elen1 para ${titleLabel}` : `Elen1 for ${titleLabel}`}</p>
            <h2>{isSpanish ? 'Mira cómo este flujo podría operar dentro de un sistema clínico conectado.' : 'See how this workflow could run inside one connected clinic system.'}</h2>
            <p>{isSpanish ? 'Agenda un recorrido enfocado y mapearemos Elen1 a tus servicios, estructura de equipo, recorrido del paciente y necesidades de reporte.' : 'Book a focused walkthrough and we will map Elen1 to your services, team structure, patient journey, and reporting needs.'}</p>
            <div className="wellness-actions">
              <Link to={toMarketPath('/book-demo')} className="btn-lime">{isSpanish ? 'Reservar una demo' : 'Book a demo'}</Link>
              <Link to={toMarketPath('/features')} className="wellness-secondary dark">{isSpanish ? 'Explorar funciones' : 'Explore features'}</Link>
            </div>
          </div>
          <img src="/landing-captures/reports-standalone.png" alt={isSpanish ? `Vista de reportes para ${titleLabel}` : `${titleLabel} reporting preview`} />
        </div>
      </section>
    </main>
  );
}

export default function LandingDetailPage({ pageKey }: { pageKey: LandingCatalogPageKey }) {
  useLandingAnimations();
  const location = useLocation();
  const { language } = useI18n();
  const { market, locale } = resolveLandingContext(location.pathname, language);
  const page = getLandingCatalogPage(pageKey, locale);
  const toMarketPath = (path: string) => buildLandingPathForMarket(path, market);
  const hasCapabilities = Boolean(page.capabilities?.length);
  const hasScreenshots = Boolean(page.screenshots?.length);
  const isFeatureHub = pageKey === 'features';
  const isSimpleAudienceHub = pageKey === 'who-we-serve';
  const isAudiencePage = page.navGroup === 'who-we-serve';
  const generatedHeroPreview = isFeatureHub
    ? {
      src: '/landing-generated/features-workflow-hero.png',
      alt: 'Modern clinic team reviewing connected Elen1 workflows across care, operations, billing, and analytics',
    }
    : isSimpleAudienceHub
      ? {
        src: '/landing-generated/who-we-serve-hero.png',
        alt: 'Clinic owner, practice manager, and practitioner using Elen1 to coordinate clinical care and business operations',
      }
      : null;
  const heroPreview = generatedHeroPreview ?? page.screenshots?.[0] ?? null;
  const heroSupportPoints = page.highlights.slice(0, 3);
  // without a preview the support points already render inside the hero surface —
  // only the remaining highlights belong in the benefits row below
  const heroBenefitItems = heroPreview ? page.highlights : page.highlights.slice(heroSupportPoints.length);
  const showHeroVisual = Boolean(heroPreview || heroSupportPoints.length);
  const hubGroups = pageKey === 'who-we-serve'
    ? getLandingTopNav(locale).find((item) => item.key === pageKey)?.groups ?? []
    : [];
  const capabilityIcons = ['ph-chart-line-up', 'ph-calendar-check', 'ph-shield-check'];
  const heroTextClass = isAudiencePage ? undefined : 'reveal-text';
  const heroVisualClass = isAudiencePage ? 'landing-detail-hero-visual' : 'landing-detail-hero-visual reveal-img';
  const isSpanish = locale === 'es';

  if (pageKey === 'pricing') {
    return (
      <LandingLayout pageKey={pageKey}>
        <PricingPageContent toMarketPath={toMarketPath} locale={locale} market={market} />
      </LandingLayout>
    );
  }

  if (pageKey === 'book-demo') {
    return (
      <LandingLayout pageKey={pageKey}>
        <BookDemoPageContent locale={locale} />
      </LandingLayout>
    );
  }

  if (pageKey === 'wellness-preventive-care' && locale !== 'es') {
    return (
      <LandingLayout pageKey={pageKey}>
        <WellnessPreventiveCarePageContent toMarketPath={toMarketPath} />
      </LandingLayout>
    );
  }

  if (pageKey === 'medspa' && locale !== 'es') {
    return (
      <LandingLayout pageKey={pageKey}>
        <MedspaPageContent toMarketPath={toMarketPath} />
      </LandingLayout>
    );
  }

  if (page.navGroup === 'who-we-serve' && pageKey !== 'who-we-serve') {
    return (
      <LandingLayout pageKey={pageKey}>
        <SolutionLandingPageContent page={page} pageKey={pageKey} toMarketPath={toMarketPath} locale={locale} />
      </LandingLayout>
    );
  }

  if (pageKey === 'features') {
    return (
      <LandingLayout pageKey={pageKey}>
        <FeaturesPageContent toMarketPath={toMarketPath} locale={locale} />
      </LandingLayout>
    );
  }

  if (pageKey === 'why-elen1') {
    return (
      <LandingLayout pageKey={pageKey}>
        <AboutUsPageContent toMarketPath={toMarketPath} locale={locale} />
      </LandingLayout>
    );
  }

  if (featureSubpageKeys.has(pageKey)) {
    return (
      <LandingLayout pageKey={pageKey}>
        <FeatureSubpageContent page={page} toMarketPath={toMarketPath} locale={locale} />
      </LandingLayout>
    );
  }

  return (
    <LandingLayout pageKey={pageKey}>
      <section className={`landing-detail-hero ${isAudiencePage ? 'landing-detail-hero-audience' : 'landing-detail-hero-feature'}`}>
        <div className="container landing-detail-shell">
          <div className={`landing-detail-hero-grid ${showHeroVisual ? 'has-visual' : ''} ${isSimpleAudienceHub ? 'is-simple' : ''}`}>
            <div className="landing-detail-hero-copy">
              <p className={`landing-detail-eyebrow${heroTextClass ? ` ${heroTextClass}` : ''}`}>{page.eyebrow}</p>
              <h1 className={heroTextClass}>{page.title}</h1>
              <p className={`landing-detail-subtitle${heroTextClass ? ` ${heroTextClass}` : ''}`}>{page.subtitle}</p>
            </div>

            {showHeroVisual ? (
              <div className={heroVisualClass}>
                <div className="landing-detail-hero-glow landing-detail-hero-glow-a" aria-hidden="true"></div>
                <div className="landing-detail-hero-glow landing-detail-hero-glow-b" aria-hidden="true"></div>
                <div className={`landing-detail-hero-surface${heroPreview ? '' : ' is-summary'}`}>
                  {heroPreview ? (
                    <div className="landing-detail-hero-preview">
                      <img src={heroPreview.src} alt={heroPreview.alt} loading="lazy" />
                    </div>
                  ) : (
                    <div className="landing-detail-hero-summary-stack">
                      {heroSupportPoints.map((item) => (
                        <article key={item} className="landing-detail-hero-summary-card">
                          <span>
                            <i className="ph ph-check"></i>
                          </span>
                          <p>{item}</p>
                        </article>
                      ))}
                    </div>
                  )}
                  <div className="landing-detail-hero-overlay-row" aria-hidden="true">
                    <span><i className="ph ph-heartbeat"></i> {isSpanish ? 'Atención clínica' : 'Clinical care'}</span>
                    <span><i className="ph ph-chart-line-up"></i> {isSpanish ? 'Operaciones del negocio' : 'Business operations'}</span>
                    <span><i className="ph ph-chat-circle-dots"></i> {isSpanish ? 'Engagement del paciente' : 'Patient engagement'}</span>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {!isSimpleAudienceHub && heroBenefitItems.length ? (
            <div className="landing-detail-hero-benefits">
              <div className="landing-detail-highlight-grid">
                {heroBenefitItems.map((item) => (
                  <article key={item} className="landing-detail-highlight-card landing-animate-card">
                    <span className="landing-detail-check">
                      <i className="ph ph-check"></i>
                    </span>
                    <p>{item}</p>
                  </article>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </section>

      {page.sections.map((section, index) => {
        const sectionPreview = page.screenshots?.[index] ?? null;
        const hasPreview = Boolean(sectionPreview);

        return (
          <section
            key={section.title}
            className={`deep-dive landing-detail-deep-dive ${index % 2 === 1 ? 'landing-detail-deep-dive-alt' : ''}`}
          >
            <div className={hasPreview ? `container deep-dive-grid ${index % 2 === 1 ? 'reverse' : ''}` : 'container landing-detail-text-shell'}>
              <div className="dd-content landing-detail-section-copy">
                <p className="landing-detail-section-kicker">{isSpanish ? 'Sección' : 'Section'} {String(index + 1).padStart(2, '0')}</p>
                <h2>{section.title}</h2>
                <p>{section.body}</p>
              </div>

              {sectionPreview ? (
                <div className="dd-image landing-detail-section-media">
                  <span className="landing-detail-section-badge">{isSpanish ? 'Dentro de Elen1' : 'Inside elen1'}</span>
                  <div className="landing-detail-section-screen-frame">
                    <img src={sectionPreview.src} alt={sectionPreview.alt} loading="lazy" />
                  </div>
                </div>
              ) : null}

              <ul className="landing-detail-action-list landing-detail-action-list-wide">
                {section.bullets.map((bullet) => (
                  <li key={bullet} className="landing-detail-action-card">
                    <span className="landing-detail-action-icon" aria-hidden="true">
                      <i className="ph ph-check"></i>
                    </span>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        );
      })}

      {hubGroups.length ? (
        <section className="landing-detail-hub-explorer">
          <div className="container">
            <div className="landing-related-header">
              <p className="landing-detail-eyebrow">{isSpanish ? 'Explorar audiencias' : 'Explore audiences'}</p>
              <h2>{isSpanish ? 'Navega por rol, etapa del negocio y especialidad para encontrar primero la ruta más relevante.' : 'Browse by role, business stage, and specialty to see the most relevant path first.'}</h2>
            </div>
            <div className="landing-detail-hub-group-grid">
              {hubGroups.map((group) => (
                <section key={group.title} className="landing-detail-hub-group-card landing-animate-card">
                  <div className="landing-detail-hub-group-header">
                    <p>{group.title}</p>
                    <span className="landing-detail-hub-count">{String(group.items.length).padStart(2, '0')} {isSpanish ? 'rutas' : 'paths'}</span>
                  </div>
                  <div className="landing-detail-hub-links">
                    {group.items.map((item) => {
                      const linkedPage = getLandingCatalogPage(item.key, locale);
                      return (
                        <Link key={item.key} to={toMarketPath(linkedPage.path)} className="landing-detail-hub-link-card">
                          <span className="landing-detail-hub-icon">
                            <i className={`ph ${item.icon}`}></i>
                          </span>
                          <span className="landing-detail-hub-copy">
                            <strong>{item.label}</strong>
                            <small>{item.description}</small>
                          </span>
                          <span className="landing-detail-hub-arrow" aria-hidden="true">
                            <i className="ph ph-arrow-up-right"></i>
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {hasCapabilities ? (
        <section className="features landing-detail-capabilities">
          <div className="container">
            <div className="landing-related-header">
              <p className="landing-detail-eyebrow">{isSpanish ? 'Cómo ayuda Elen1' : 'How elen1 helps'}</p>
              <h2>{isSpanish ? 'Apoya el trabajo del que esta audiencia es responsable cada día.' : 'Support the work this audience is accountable for every day.'}</h2>
            </div>
            <div className="landing-detail-capabilities-grid">
              {page.capabilities?.map((capability, index) => (
                <article key={capability.title} className="landing-detail-capability-card landing-animate-card">
                  <span className="landing-detail-capability-mark">
                    <i className={`ph ${capabilityIcons[index % capabilityIcons.length]}`}></i>
                  </span>
                  <h3>{capability.title}</h3>
                  <p>{capability.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {hasScreenshots ? (
        <section className="landing-detail-gallery">
          <div className="container">
            <div className="landing-related-header">
              <p className="landing-detail-eyebrow">{isSpanish ? 'En el producto' : 'In the product'}</p>
              <h2>{isSpanish ? 'Flujos relevantes ya disponibles en Elen1.' : 'Relevant workflows already available in elen1.'}</h2>
            </div>
            <div className="landing-detail-gallery-grid">
              {page.screenshots?.map((screenshot, index) => (
                <article
                  key={`${screenshot.src}-${index}`}
                  className={`landing-detail-gallery-card ${index % 2 === 0 ? 'media-left' : 'media-right'}`}
                >
                  <div className="landing-detail-gallery-image-shell">
                    <div className="landing-detail-gallery-screen-frame">
                      <img src={screenshot.src} alt={screenshot.alt} loading="lazy" />
                    </div>
                  </div>
                  <div className="landing-detail-gallery-copy">
                    <span className="landing-detail-gallery-badge">{isSpanish ? 'Flujo' : 'Workflow'} {String(index + 1).padStart(2, '0')}</span>
                    <h3>{screenshot.title}</h3>
                    <p>{screenshot.body}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="landing-related-pages">
        <div className="container">
          <div className="landing-related-header">
            <p className="landing-detail-eyebrow">{isSpanish ? 'Páginas relacionadas' : 'Related pages'}</p>
            <h2>{isSpanish ? 'Explora flujos relacionados y rutas de compra.' : 'Explore adjacent workflows and buying paths.'}</h2>
          </div>
          <div className="landing-related-grid">
            {page.related.map((relatedKey) => {
              const relatedPage = getLandingCatalogPage(relatedKey, locale);
              return (
                <Link key={relatedKey} to={toMarketPath(relatedPage.path)} className="landing-related-card landing-animate-card">
                  <p className="landing-related-card-eyebrow">{relatedPage.eyebrow}</p>
                  <h3>{relatedPage.title}</h3>
                  <p>{page.relatedDescriptions?.[relatedKey] ?? relatedPage.subtitle}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </LandingLayout>
  );
}
